import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from 'react';

type DiscountTypeType = 'fixed' | 'percentage';
export type BundleSetting = {
  Size: string;
  Label: string;
  Price: string;
  VariantId: string;
  MinSize?: string;
  MaxSize?: string;
  DynamicDiscountType?: DiscountTypeType;
  DynamicDiscountAmount?: number;
  DynamicDiscountCode?: string;
  DynamicDiscountId?: string;
};

type UpdateBundleSettingType = (setting: BundleSetting) => void;

const BundleSettingContext = createContext<
  | {
      bundleSetting: BundleSetting;
      updateBundleSetting: UpdateBundleSettingType;
    }
  | undefined
>(undefined);

const bundleSettingReducer = (state, action) => {
  switch (action.type) {
    case 'update': {
      return {
        setting: action.setting,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const BundleSettingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bundleSettingReducer, {
    setting: undefined,
  });
  const updateBundleSetting = useCallback(
    (setting) => dispatch({ type: 'update', setting }),
    [dispatch],
  );
  const bundleSetting: BundleSetting = useMemo(() => state.setting, [
    state.setting,
  ]);
  const value = { bundleSetting, updateBundleSetting };
  return (
    <BundleSettingContext.Provider value={value}>
      {children}
    </BundleSettingContext.Provider>
  );
};

const useBundleSetting = () => {
  const context = useContext(BundleSettingContext);
  if (context == undefined) {
    throw new Error(
      'useBundleSetting must be used within a BundleSettingProvider',
    );
  }
  return context;
};

export { BundleSettingProvider, useBundleSetting };
